/**
 * useQuickAddCrashSightMcp — 一键添加 CrashSight MCP Server 的共享 Hook
 *
 * 检测用户是否已配置 CrashSight MCP Server（通过 serverConfig.url 匹配，
 * 比按名称更可靠，因为用户可能重命名），若未配置则提供一键添加能力：
 *   1. 查找/创建/续期名为 "CrashClaw" 的 MCP 令牌
 *   2. 用令牌的 apiKey 自动创建 CrashSight MCP Server
 *
 * 供 CrashClawMcpPanel 和 CrashClawAgentPanel 共用。
 */
import { useState, useMemo } from 'react';
import { message } from 'antd';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import { EnvUtil } from 'utils/env-util';
// ======================== Constants ========================
/** 自动创建的令牌固定名称，用于查找和识别 */
export const CRASHCLAW_TOKEN_NAME = 'CrashClaw';
/**
 * 所有可能的 CrashSight MCP Server URL（各环境域名），
 * 用于检测用户是否已配置过 CrashSight MCP（匹配任意一个即视为已配置）。
 */
const CRASHSIGHT_MCP_KNOWN_URLS = new Set([
    'https://crashsight.qq.com/mcp',
    'https://test.crashsight.qq.com/mcp',
    'https://crashsight.wetest.net/mcp',
    'https://crashsight.woa.com/mcp',
]);
/** 根据当前环境获取 CrashSight MCP Server URL */
export function getCrashSightMcpUrl() {
    const hostname = window.location.hostname;
    if (hostname === 'test.crashsight.qq.com') {
        return 'https://test.crashsight.qq.com/mcp';
    }
    if (hostname === 'crashsight.woa.com') {
        return 'https://crashsight.qq.com/mcp';
    }
    if (EnvUtil.isChinaEnv()) {
        return 'https://crashsight.qq.com/mcp';
    }
    return 'https://crashsight.wetest.net/mcp';
}
/** 检查一个 URL 是否属于 CrashSight MCP（任意环境） */
export function isCrashSightMcpUrl(url) {
    return CRASHSIGHT_MCP_KNOWN_URLS.has(url);
}
/** 自动创建/续期令牌的有效期（天）。后端 createToken / 单次 renewToken 上限均为 365，不能超 */
const TOKEN_EXPIRE_DAYS = 360;
const MCP_TOKEN_API_PREFIX = '/api/mcp';
const CRASHCLAW_API_PREFIX = '/redir/api/crashclaw';
// ======================== Helpers ========================
/**
 * 通过 serverConfig.url 匹配检测列表中是否存在 CrashSight MCP Server。
 * 检查所有已知环境域名，不按 serverName 匹配（用户可能重命名）。
 */
function hasCrashSightMcpInList(servers) {
    return servers.some(s => {
        const url = s.serverConfig?.url || '';
        return isCrashSightMcpUrl(url);
    });
}
/** 生成不重复的 Server 名称（Windows 风格去重："Name (2)"） */
function generateUniqueName(baseName, existingNames) {
    if (!existingNames.has(baseName))
        return baseName;
    let i = 2;
    while (existingNames.has(`${baseName} (${i})`))
        i++;
    return `${baseName} (${i})`;
}
// ======================== Hook ========================
/**
 * @param serverList 当前用户的完整 MCP Server 列表（含禁用的），用于检测 CrashSight MCP 是否存在
 * @param onSuccess  一键添加成功后的回调，参数为新创建的 mcpUid，供调用方刷新列表/触发连通性测试
 */
export function useQuickAddCrashSightMcp(serverList, onSuccess) {
    const [loading, setLoading] = useState(false);
    const showAlert = useMemo(() => !hasCrashSightMcpInList(serverList), [serverList]);
    async function handleQuickAdd() {
        if (loading)
            return;
        setLoading(true);
        try {
            // Step 1: 确保存在有效的 CrashClaw 令牌
            const { json: tokenListJson } = await RestHelper.post(`${MCP_TOKEN_API_PREFIX}/listTokens`);
            const tokens = tokenListJson.data || [];
            let targetToken = tokens.find(t => t.name === CRASHCLAW_TOKEN_NAME) || null;
            if (!targetToken) {
                // 没有 CrashClaw 令牌，创建一个 360 天有效期的
                const { json: createJson } = await RestHelper.post(`${MCP_TOKEN_API_PREFIX}/createToken`, {
                    name: CRASHCLAW_TOKEN_NAME,
                    expireDays: TOKEN_EXPIRE_DAYS,
                });
                targetToken = createJson.data;
            }
            else if (targetToken.expiresAtMillis < Date.now()) {
                // 令牌已过期，续期 360 天（apiKey 不变）
                await RestHelper.post(`${MCP_TOKEN_API_PREFIX}/renewToken`, {
                    id: targetToken.id,
                    expireDays: TOKEN_EXPIRE_DAYS,
                });
            }
            if (!targetToken?.apiKey) {
                throw new Error('Failed to obtain CrashClaw token');
            }
            // Step 2: 用令牌创建 CrashSight MCP Server（配置格式与预定义模板一致）
            const existingNames = new Set(serverList.map(s => s.serverName));
            const serverName = generateUniqueName('CrashSight MCP', existingNames);
            const serverConfig = {
                headers: { 'X-CSMCP-API-KEY': targetToken.apiKey },
                timeout: 60,
                transportType: 'streamable-http',
                url: getCrashSightMcpUrl(),
            };
            const rsp = await RestHelper.mustPost(`${CRASHCLAW_API_PREFIX}/createMcpServer`, {
                serverName,
                transportType: 'STREAMABLE_HTTP',
                serverConfig,
                description: null,
            });
            const createdMcpUid = rsp.json.data.mcpUid;
            message.success(ze('CrashSight MCP 添加成功', 'CrashSight MCP added successfully'));
            await onSuccess(createdMcpUid);
        }
        catch (e) {
            console.error('Quick add CrashSight MCP failed', e);
            message.error(ze('一键添加失败，请稍后重试', 'Quick add failed, please try again later'));
        }
        finally {
            setLoading(false);
        }
    }
    return { showAlert, loading, handleQuickAdd };
}
